import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import CardHeader from "reactstrap/lib/CardHeader";
import LineChart from "src/components/atoms/LineChart/LineChart";
import Switch from "src/components/atoms/Switch/Switch";
import { expandJSON, onFetch } from "src/helpers/helper";
import useWindowSize from "src/hooks/useWindowSize";
import {
  AET_DASHBOARD,
  FLEET_FUEL_REPORT_URL,
  SAVE_CALCULATED_FUEL,
} from "src/redux/config/links";
import SelectCompany from "./contents/SelectCompany/SelectCompany";
import SelectFleet from "./contents/SelectFleet/SelectFleet";
import FleetCarbon from "./contents/FleetCarbon/FleetCarbon";
import FormClaimDecarbonization from "./contents/FormClaimDecarbonization/FormClaimDecarbonization";
import { wording } from "src/constants/constants";

function getRandomHexColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

const AETChart = () => {
  const [claimDecarbon, setClaimDecarbon] = useState([]);
  const [modalClaimDecarbon, setModalClaimDecarbon] = useState(null);

  const { authStore } = useSelector((state) => ({
    authStore: state.authStore,
  }));
  const { width } = useWindowSize();
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [selectedFleet, setSelectedFleet] = useState([]);
  const [calFuel, setCalFuel] = useState(true);
  const [isFetchingAllFuel, setIsFetchingAllFuel] = useState(false);
  const [loadFetchingAllFuel, setLoadFetchingAllFuel] = useState(true);
  const [isCalAllFuel, setIsCalAllFuel] = useState(false);

  const [page, setPage] = useState({
    of: 1,
    size: 20,
  });
  const [loadContent, setLoadContent] = useState(false);
  const [userList, setUserList] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const [loadFleet, setLoadFleet] = useState(true);

  const onLoadAllChart = useCallback(
    ({ page, sort, filter, show = [], groupby = [] }, cb = () => null) => {
      let newFilter = {};
      Object.keys(filter).forEach((item) => {
        newFilter = { ...newFilter, [item]: encodeURI(`${filter[item]}`) };
      });

      const query = expandJSON({ page, sort, filter: newFilter, show, groupby })
        .map((item) => `${item.label}=${item.value}`)
        .join("&");
      axios({
        url: `${AET_DASHBOARD}?${query}`,
        method: "POST",
        headers: {
          Authorization: authStore.authData.result.token,
        },
      })
        .then((result) => {
          setUserList((prev) => ({
            ...(!!prev ? prev : {}),
            ["All"]: {
              color: getRandomHexColor(),
              ...(!!prev && !!prev["All"] ? prev["All"] : {}),
              result: {
                data: [
                  ...(!!prev && !!prev["All"] ? prev["All"].result.data : []),
                  ...result.data.result.data,
                ],
                total: result.data.result.total,
              },
            },
          }));

          setLoadFetchingAllFuel(true);
          cb();
        })
        .catch(() => {
          cb();
        });
    },
    [authStore.authData.result.token]
  );

  const onUserList = useCallback(
    ({ page, sort, filter, show = [], groupby = [] }, cb = () => null) => {
      let newFilter = {};
      Object.keys(filter).forEach((item) => {
        newFilter = { ...newFilter, [item]: encodeURI(`${filter[item]}`) };
      });

      const query = expandJSON({ page, sort, filter: newFilter, show, groupby })
        .map((item) => `${item.label}=${item.value}`)
        .join("&");
      axios({
        url: `${AET_DASHBOARD}?${query}`,
        method: "POST",
        headers: {
          Authorization: authStore.authData.result.token,
        },
      })
        .then((result) => {
          setUserList((prev) => ({
            ...(!!prev ? prev : {}),
            [JSON.stringify(filter)]: {
              color: getRandomHexColor(),
              ...(!!prev && !!prev[JSON.stringify(filter)]
                ? prev[JSON.stringify(filter)]
                : {}),
              result: {
                data: [
                  ...(!!prev && !!prev[JSON.stringify(filter)]
                    ? prev[JSON.stringify(filter)].result.data
                    : []),
                  ...result.data.result.data,
                ],
                total: result.data.result.total,
              },
            },
          }));

          setLoadFetchingAllFuel(true);
          cb();
        })
        .catch(() => {
          cb();
        });
    },
    [authStore.authData.result.token]
  );

  const generateToPoints = (arr) => {
    return (!!arr ? Object.keys(arr) : []).map((key) => {
      return {
        color: arr[key].color,
        label: key !== "All" ? JSON.parse(key).leftJoin_fleet_reg_number : "",
        points: (!!arr[key] ? arr[key].result.data : [])
          .sort(
            (a, b) =>
              new Date(a.assigned_do_date).getTime() -
              new Date(b.assigned_do_date).getTime()
          )
          .map((item) => ({
            x: new Date(item.assigned_do_date).getTime(),
            y:
              item.calculated_carbon === "loading" ? 0 : item.calculated_carbon,
          }))
          .map((item, index, self) => {
            const sum = self
              .slice(0, index + 1)
              .reduce((acc, p) => acc + p.y, 0);
            return { ...item, y: sum };
          }),
      };
    });
  };

  const generateClaimDecarbonPoints = (arr, value) => {
    const [prevArr] = generateToPoints(arr).filter(
      (item) => item.label === value.from
    );

    if (prevArr) {
      const afterDecarbon = prevArr.points
        .filter((item) => item.x >= value.x)
        .reduce((acc, point, index) => {
          if (index === 0) {
            // first element: initialize newY as y
            acc.push({ ...point, newY: point.y });
          } else {
            const prev = acc[index - 1];
            acc.push({
              ...point,
              newY:
                (point.y - prev.y) * ((100 - Number(value.claim)) / 100) +
                prev.newY,
            });
          }
          return acc;
        }, [])
        .map((item) => ({ x: item.x, y: item.newY }));

      return afterDecarbon;
    }

    return [];
  };

  const fetchFuelConsumption = useCallback(
    (name, id_fleet, startDate, endDate, id_do, cb = () => null) => {
      onFetch(
        !!authStore.authData?authStore.authData.result.token:null,
        {
          url: FLEET_FUEL_REPORT_URL,
          data: {
            id: id_fleet,
            start_date: startDate,
            end_date: endDate,
          },
          type: "json",
        },
        {
          beforeSend: () => {
            setUserList((prev) => ({
              ...prev,
              [name]: {
                ...prev[name],
                result: {
                  ...prev[name].result,
                  data: (!!prev[name].result ? prev[name].result.data : []).map(
                    (item) =>
                      item.id === id_do
                        ? {
                            ...item,
                            calculated_fuel_consumption: "loading",
                            calculated_carbon: "loading",
                          }
                        : item
                  ),
                },
              },
            }));
          },
          success: (res) => {
            setUserList((prev) => ({
              ...prev,
              [name]: {
                ...prev[name],
                result: {
                  ...prev[name].result,
                  data: prev[name].result.data.map((item) =>
                    item.id === id_do
                      ? {
                          ...item,
                          calculated_fuel_consumption:
                            res.data.fuel_consumption,
                          calculated_carbon: res.data.carbon,
                        }
                      : item
                  ),
                },
              },
            }));

            onFetch(
        !!authStore.authData?authStore.authData.result.token:null,
              {
                url: SAVE_CALCULATED_FUEL,
                data: {
                  delivery_order_id: id_do,
                  calculated_fuel_consumption: res.data.fuel_consumption,
                  calculated_carbon: res.data.carbon,
                },
                type: "json",
              },
              {
                beforeSend: () => null,
                success: () => cb(),
                error: () => cb(),
              }
            );
          },
          error: () => {
            setUserList((prev) => ({
              ...prev,
              [name]: {
                ...prev[name],
                result: {
                  ...prev[name].result,
                  data: prev[name].result.data.map((item) =>
                    item.id === id_do
                      ? {
                          ...item,
                          calculated_fuel_consumption: null,
                          calculated_carbon: null,
                        }
                      : item
                  ),
                },
              },
            }));
            cb();
          },
        }
      );
    },
    []
  );

  const infiniteFetchFuelConsump = useCallback(
    (arr, name, index, cb) => {
      if (arr.length > 0) {
        const current = arr[index];

        if (!!current.id_fleet) {
          fetchFuelConsumption(
            name,
            current.id_fleet,
            current.assigned_do_date,
            current.end_assigned_do_date,
            current.id,
            () => {
              if (arr.length > index + 1) {
                infiniteFetchFuelConsump(arr, name, index + 1, cb);
              } else {
                cb();
              }
            }
          );
        } else {
          if (arr.length > index + 1) {
            infiniteFetchFuelConsump(arr, name, index + 1, cb);
          } else {
            cb();
          }
        }
      } else {
        cb();
      }
    },
    [fetchFuelConsumption]
  );

  const handleMultipleInfinite = useCallback(
    (arr, i, cb) => {
      if (Object.keys(arr).length > 0) {
        infiniteFetchFuelConsump(
          userList[Object.keys(arr)[i]].result.data.filter(
            (item) =>
              item.calculated_fuel_consumption === null ||
              item.calculated_carbon === null
          ),
          Object.keys(arr)[i],
          0,
          () => {
            // done
            if (!!Object.keys(arr)[i + 1]) {
              handleMultipleInfinite(arr, i + 1, cb);
            } else {
              cb();
            }
          }
        );
      } else {
        cb();
      }
    },
    [infiniteFetchFuelConsump, userList]
  );

  const handleMultiple = useCallback(
    (arr, i, cb) => {
      if (arr.length > 0) {
        onUserList(
          {
            page: page,
            sort: [
              {
                by: "assigned_do_date",
                order: "asc",
              },
            ],
            filter: {
              leftJoin_fleet_reg_number: arr[i].value,
            },
            show: [
              "id",
              "id_fleet",
              "leftJoin_fleet_reg_number",
              "leftJoin_company_name",
              "assigned_do_date",
              "end_assigned_do_date",
              "calculated_fuel_consumption",
              "calculated_carbon",
            ],
          },
          () => {
            if (!!arr[i + 1]) {
              handleMultiple(arr, i + 1, cb);
            } else {
              cb();
            }
          }
        );
      } else {
        cb();
      }
    },
    [onUserList, page]
  );

  const handleAll = useCallback(
    (cb) => {
      onLoadAllChart(
        {
          page: page,
          sort: [
            {
              by: "assigned_do_date",
              order: "asc",
            },
          ],
          filter: {},
          show: [
            "id",
            "id_fleet",
            "leftJoin_fleet_reg_number",
            "leftJoin_company_name",
            "assigned_do_date",
            "end_assigned_do_date",
            "calculated_fuel_consumption",
            "calculated_carbon",
          ],
        },
        cb
      );
    },
    [onLoadAllChart, page]
  );

  useEffect(() => {
    if (loadContent) {
      setLoadContent(false);
      setLoading(true);

      if (selectedCompany.findIndex((i) => i.value === "All") >= 0) {
        handleAll(() => {
          setLoading(false);
        });
      } else {
        handleMultiple(selectedFleet, 0, () => {
          setLoading(false);
        });
      }
    }
  }, [handleMultiple, loadContent, selectedFleet, selectedCompany, handleAll]);

  useEffect(() => {
    if (calFuel && !!userList && !isLoading && loadFetchingAllFuel) {
      setLoadFetchingAllFuel(false);
      setIsFetchingAllFuel(true);
    }
  }, [calFuel, userList, loadFetchingAllFuel, isLoading]);

  useEffect(() => {
    if (isFetchingAllFuel) {
      setIsFetchingAllFuel(false);
      setIsCalAllFuel(true);
      handleMultipleInfinite(userList, 0, () => {
        setIsCalAllFuel(false);
      });
    }
  }, [handleMultipleInfinite, isFetchingAllFuel, userList]);

  return (
    <>
      <Card>
        <CardHeader>Delivery Order</CardHeader>
        <CardBody>
          <Row>
            <Col>
              <SelectCompany
                selectedCompany={selectedCompany}
                onChangeSelectedCompany={(e, clicked) => {
                  // setFilter({ ...filter, leftJoin_company_name: e.value });
                  if (clicked.value === "All") {
                    setUserList(null);
                    setSelectedCompany([{ value: "All", label: "All" }]);
                    setLoadContent(true);
                  } else {
                    let dt = userList;

                    if (!!dt) {
                      delete dt["All"];
                      setUserList({ ...dt });
                    }

                    setSelectedCompany([...e].filter((i) => i.value !== "All"));
                    setSelectedFleet([]);
                    setLoadFleet(true);
                  }
                }}
                onRemoveSelectedCompany={(e) => {
                  setSelectedCompany(
                    [...selectedCompany].filter((sub) => sub.value !== e.value)
                  );
                  setLoadFleet(true);

                  setSelectedFleet(
                    [...selectedFleet].filter((sub) => sub.value !== e.value)
                  );

                  let dt = userList;

                  if (e.value === "All") {
                    delete dt["All"];
                  } else {
                    delete dt[
                      JSON.stringify({ leftJoin_fleet_reg_number: e.value })
                    ];
                  }

                  setUserList({ ...dt });
                }}
              />
            </Col>
            <Col>
              <SelectFleet
                disabled={
                  selectedCompany.length <= 0 ||
                  selectedCompany.findIndex((i) => i.value === "All") >= 0
                }
                selectedCompany={selectedCompany}
                loadFleet={loadFleet}
                setLoadFleet={setLoadFleet}
                onChangeSelectedFleet={(e, allList) => {
                  if (e.findIndex((s) => s.value === "All") >= 0) {
                    setSelectedFleet(
                      [...allList.result.data].map((item) => ({
                        label: `${item.leftJoin_company_name} - ${item.leftJoin_fleet_reg_number}`,
                        value: item.leftJoin_fleet_reg_number,
                      }))
                    );
                  } else {
                    setSelectedFleet([...e]);
                  }
                  setPage({ ...page, of: 1 });
                  setUserList(null);
                  setLoadContent(true);
                  setClaimDecarbon([]);
                }}
                selectedFleet={selectedFleet}
                onRemoveSelectedFleet={(e) => {
                  setSelectedFleet(
                    [...selectedFleet].filter((sub) => sub.value !== e.value)
                  );

                  let dt = userList;

                  delete dt[
                    JSON.stringify({ leftJoin_fleet_reg_number: e.value })
                  ];

                  setUserList({ ...dt });
                }}
              />
            </Col>
          </Row>

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            Calculate all Fuel and Carbon:{" "}
            <Switch
              toggle={calFuel}
              onChange={(e) => setCalFuel(e)}
              size={0.5}
            />
          </div>
          <>
            {(!!userList
              ? Object.keys(userList).map((item) => ({
                  ...userList[item],
                  fleet:
                    item === "All"
                      ? "All"
                      : JSON.parse(item).leftJoin_fleet_reg_number,
                }))
              : []
            ).map((item, i) => (
              <div key={i}>
                <div
                  style={{
                    background: item.color,
                    width: 15,
                    display: "inline-block",
                  }}
                >
                  &nbsp;
                </div>{" "}
                {item.fleet !== "All" && selectedFleet.length > 0
                  ? selectedFleet[
                      selectedFleet.findIndex((s) => s.value === item.fleet) ??
                        0
                    ].label
                  : null}{" "}
                {item.fleet === "All" ? "All" : null}
                <FleetCarbon fleet={item.fleet} />
              </div>
            ))}
            <LineChart
              datasets={[
                ...generateToPoints(userList),
                ...claimDecarbon.map((item) => {
                  return {
                    label: item.label,
                    color: item.color,
                    points: generateClaimDecarbonPoints(userList, item),
                  };
                }),
              ]}
              width={width - 210}
              height={300}
              xLabel={wording["DO"]+" Start Date"}
              yLabel="Carbon"
              customXValue={(value) => {
                return <>{moment(value).format("DD MMM YYYY")}</>;
              }}
              customTooltip={({ dataset, x, y }) => {
                const index = selectedFleet.findIndex(
                  (s) => s.value === dataset
                );
                return (
                  <>
                    <div>
                      <strong>
                        {selectedFleet.length > 0 && index >= 0
                          ? selectedFleet[index ?? 0].label
                          : dataset}{" "}
                      </strong>
                    </div>
                    <div>
                      <strong>X:</strong> {moment(x).format("DD MMM YYYY")}
                    </div>
                    <div>
                      <strong>Y:</strong> {y}
                    </div>
                  </>
                );
              }}
              loading={isLoading || isCalAllFuel}
              onLoadMore={() => {
                setPage((p) => ({ ...p, of: p.of + 1 }));
                setLoadContent(true);
              }}
              onClickPoint={(value) => {
                setModalClaimDecarbon({
                  ...value,
                  dataset: value.dataset.split(" - ")[0],
                });
              }}
            />
          </>
        </CardBody>
      </Card>

      <Modal
        size="md"
        isOpen={!!modalClaimDecarbon}
        toggle={() => setModalClaimDecarbon(null)}
        backdrop="static"
      >
        <ModalHeader toggle={() => setModalClaimDecarbon(null)}>
          Claim Decarbonization
        </ModalHeader>
        <ModalBody>
          {!!modalClaimDecarbon ? (
            <FormClaimDecarbonization
              data={modalClaimDecarbon}
              onClose={() => setModalClaimDecarbon(null)}
              onSubmit={(value) => {
                setClaimDecarbon([
                  ...claimDecarbon.filter(
                    (item) => item.label !== value.dataset + " - Claim Decarbon"
                  ),
                  {
                    ...value,
                    from: value.dataset,
                    label: value.dataset + " - Claim Decarbon",
                    color: getRandomHexColor(),
                  },
                ]);

                setModalClaimDecarbon(null);
              }}
            />
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};

export default AETChart;
